import type { ComponentProps } from "react";

import { cx } from "./cx";

/**
 * `danger` is a modifier, not a fourth variant, because the stylesheet it was translated
 * from treated it as one: it is a colour override on top of whatever the button already
 * is, and both `primary danger` (Stop & get the summary) and `ghost danger` (Delete
 * template and key) shipped. A four-way variant could spell only one of them.
 */
export type ButtonVariant = "primary" | "ghost" | "link";

/**
 * The two drawn heights: the design's control (56px) and the small action (32px) used where
 * a button sits inside a card's own text -- the identity card's Voice profile. The small one
 * keeps the button register (mono caps, the control's label size) and takes its margins
 * from the spacing scale, so it reads as a control rather than as a status chip.
 */
export type ButtonSize = "small";

export interface ButtonProps extends ComponentProps<"button"> {
  variant?: ButtonVariant;
  danger?: boolean;
  size?: ButtonSize;
}

/**
 * Only what every button has, and nothing any variant contradicts. See `look` for why the
 * split is drawn exactly here.
 *
 * Radius and font family are variant-owned. Primary and tonal actions are pills in the
 * flat-pill treatment; a shared utility a variant overrides is decided by Tailwind's emit
 * order rather than by the caller.
 */
const COMMON =
  "cursor-pointer button-transition enabled:active:button-press " +
  "motion-reduce:enabled:active:transform-none disabled:opacity-45 disabled:cursor-default";

/**
 * The box a solid button occupies. `min-h-control` is 56px, the design's floor for a large,
 * high-contrast touch target: one-handed, at a table. Labels are set in the instrument
 * register -- mono caps, tracked -- because a button names an action the way a panel
 * heading names its data. The small action keeps that register at `min-h-control-small`
 * (32px): the same label in a pill that sits inside a card's text, not a badge.
 */
const SOLID = "font-code text-button font-semibold uppercase tracking-label border";
const SOLID_BOX = "rounded-pill px-pad py-panel min-h-control";
const SMALL_BOX = "rounded-pill px-gutter py-tight min-h-control-small";

/**
 * Resolved here rather than by stacking classes and hoping.
 *
 * [V] Utilities all have the same specificity, so `border-transparent border-line-strong`
 * is decided by which one Tailwind emitted first -- not by the order they appear in the
 * attribute. Written as a shared base plus per-variant overrides, this rendered ghost
 * buttons with no border and link buttons 48px tall with the wrong weight, because the base
 * happened to be emitted last. The rule that came out of it: a shared string may not
 * contain any property a variant sets. Each set below is complete and self-consistent, and
 * the only property named twice in one of them is one nothing else names.
 */
function look(variant: ButtonVariant, danger: boolean, size?: ButtonSize): string {
  if (variant === "link") {
    // Not a box at all: no border, no minimum, and quieter than the text it sits beside.
    // `border-none` rather than `border-0` -- zero width with a style still computes as a
    // border, and the point is that this one is not one. Stays in the body face: a link
    // reads as prose that happens to act, not as an instrument label. The small one is the
    // footer's caption size -- Delete account, just above the footer's links on Account.
    return cx(
      "border-none p-link min-h-0 bg-transparent underline underline-offset-3",
      size === "small" ? "text-mono-row font-normal" : "text-link font-medium",
      danger ? "text-danger" : "text-ink-soft"
    );
  }
  if (variant === "primary") {
    return cx(
      SOLID,
      // The full-width primary action is a pill like the shrink-to-fit outline action.
      // `danger` recolours rather than re-weights: ending a session for everybody is
      // destructive, and it should read as the error colour, not just as "the main button".
      size === "small" ? SMALL_BOX : `${SOLID_BOX} w-full`,
      danger ? "border-transparent bg-danger text-accent-ink" : "border-transparent bg-accent text-accent-ink"
    );
  }
  return cx(
    SOLID,
    size === "small" ? SMALL_BOX : SOLID_BOX,
    danger ? "border-danger bg-surface-2 text-danger" : "border-line-strong bg-surface-2 text-ink"
  );
}

/**
 * `type="button"` by default. There are no forms in this app, so a button that defaults to
 * `submit` has nothing to submit and one that finds itself inside a form later would
 * navigate instead of doing its job.
 */
export function Button({ variant = "primary", danger = false, size, className, ...rest }: ButtonProps) {
  return <button type="button" className={cx(buttonClass(variant, danger, size), className)} {...rest} />;
}

/**
 * The same classes for a button built outside React -- the boot overlay's retry, which has
 * to work when the app never mounted.
 */
export function buttonClass(variant: ButtonVariant = "primary", danger = false, size?: ButtonSize): string {
  return cx(COMMON, look(variant, danger, size));
}
